import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";
import type { ProjectDetail } from "../../shared/types/api";
import { CATEGORY_LABELS, CATEGORY_ORDER, formatHours } from "./projectVocabulary";

/**
 * Drafts a baseline from the project's current plan (§25, §27).
 *
 * <p>The panel shows exactly what will be captured before anything is committed. A snapshot whose
 * contents are only visible after approval is one nobody can check, and approval is the point at
 * which it stops being changeable.
 *
 * <p>A reason is required for every version after the first. V1 is the original commitment and
 * explains itself; a revision changes something already agreed, and §27 asks that to be stated.
 */
export function CreateBaselineDrawer({
  project,
  isRevision,
  open,
  onClose,
  onSaved,
}: {
  project: ProjectDetail;
  isRevision: boolean;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setChangeSummary("");
      setError(null);
    }
  }, [open]);

  const close = () => {
    setError(null);
    onClose();
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/projects/${project.id}/baselines`, {
        reason: reason || undefined,
        changeSummary: changeSummary || undefined,
      });
      onSaved();
      close();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not create the baseline.");
    } finally {
      setSaving(false);
    }
  };

  const budgeted = CATEGORY_ORDER.filter((category) => project.budgetHours[category]);

  return (
    <Drawer
      open={open}
      onClose={close}
      title={isRevision ? "Revise the baseline" : "Create the baseline"}
      subtitle={project.name}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={isRevision && reason.trim() === ""}
          >
            {saving ? "Creating…" : "Create draft"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          {isRevision
            ? "The current baseline stays exactly as it was signed. This draft becomes the commitment only once approved, and the previous version remains readable."
            : "This captures what is being committed to. Once approved it cannot be edited — changing the commitment later means approving a new version."}
        </InlineAlert>

        {isRevision && (
          <Field
            label="Why is the commitment changing?"
            required
            hint="Recorded permanently against this version (§27)."
          >
            <Textarea
              rows={2}
              autoFocus
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Scope change: two extra payment providers"
            />
          </Field>
        )}

        <Field label="Change summary" hint="Optional. What moved, and by how much.">
          <Textarea
            rows={2}
            value={changeSummary}
            onChange={(event) => setChangeSummary(event.target.value)}
            placeholder="Development +450h"
          />
        </Field>

        <section className="border-t border-ink-100 pt-4">
          <h3 className="mb-2 text-sm font-semibold text-ink-900">What will be captured</h3>

          <div className="overflow-hidden rounded-lg border border-ink-200">
            <Row label="Approved hours" value={formatHours(project.approvedHours)} strong />
            <Row label="Start" value={project.plannedStartDate} />
            <Row label="Finish" value={project.plannedEndDate} />
            <Row label="Project manager" value={project.projectManagerName ?? "not assigned"} />
            <Row label="Risk level" value={project.riskLevel} />
          </div>

          <p className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wider text-ink-400">
            Budget by category
          </p>
          <div className="overflow-hidden rounded-lg border border-ink-200">
            {budgeted.length === 0 ? (
              <p className="px-3 py-2 text-sm text-ink-500">
                No budget has been entered, so the baseline would commit to zero hours.
              </p>
            ) : (
              budgeted.map((category) => (
                <Row
                  key={category}
                  label={CATEGORY_LABELS[category]}
                  value={formatHours(project.budgetHours[category] ?? 0)}
                />
              ))
            )}
          </div>

          <p className="mt-3 text-xs text-ink-400">
            Milestones and current project members are captured too, with their names as they read
            today.
          </p>
        </section>
      </div>
    </Drawer>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0">
      <span className="text-ink-500">{label}</span>
      <span className={`tnum ${strong ? "font-semibold text-ink-900" : "text-ink-800"}`}>
        {value}
      </span>
    </div>
  );
}
